import type { RunSkuSummary } from "@nova-ai/shared"
import { AlertTriangle, Archive, Ban, Check, Wrench } from "lucide-react"

import { SegmentedControl, type SegmentedOption } from "../ui/segmented-control"

// Counts describe the inspected membership scope. Unloaded and unsafe evidence
// stays unknown. Selecting a verdict never substitutes saved validation counts.

export type VerdictFilter = "all" | "ok" | "nok"

// The three NOK cohorts, revealed once NOK is the active verdict. `fixable` and
// `excluded` are the non-parked split (nokFixable / nokBlocked); `set-aside` is
// the parked bucket. They partition the NOK total (see RunSkuSummary).
export type NokCohort = "fixable" | "excluded" | "set-aside"

const REASON_LABELS: Record<string, string> = {
  brand_not_allowed: "Brand not allowed",
  ean_invalid: "Invalid EAN",
  price_unknown: "No price",
  bulk_pack_only: "Bulk pack only",
  country_version_mismatch: "Country-version mismatch",
  locally_banned: "Locally banned",
  long_term_unavailable: "Long-term unavailable",
  delisted_out_of_stock: "Delisted / out of stock",
  no_combined_eligible_variants: "No eligible variant combination",
  no_catalog_row: "No catalog data",
  no_category: "No marketplace category",
  no_writer: "No writer",
  no_facts: "No product facts",
  missing_required: "Missing required attribute",
  category_not_leaf: "Category not a leaf",
  nutrition_table_missing: "Nutrition table missing",
  variant_differentiator_missing: "No variant differentiator",
  variant_title_duplicate: "Duplicate variant title",
  no_feed_row: "Produces no feed row",
}

export function reasonLabel(reason: string): string {
  const known = REASON_LABELS[reason]
  if (known) {
    return known
  }
  const below = /^price_below_(.+)$/.exec(reason)
  if (below) {
    return `Price below ${below[1]}`
  }
  return reason.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase())
}

export function RunVerdictTabs({
  summary,
  value,
  onChange,
}: {
  summary: RunSkuSummary
  value: VerdictFilter
  onChange: (value: VerdictFilter) => void
}) {
  const { total, ok, nok, pending } = summary

  const verdictOptions: readonly SegmentedOption<VerdictFilter>[] = [
    { value: "all", label: "All", count: total },
    { value: "ok", label: "OK", icon: Check, iconClassName: "text-success", count: ok },
    {
      value: "nok",
      label: "NOK",
      icon: AlertTriangle,
      iconClassName: "text-destructive",
      count: nok,
    },
  ]

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <SegmentedControl
        options={verdictOptions}
        value={value}
        onChange={onChange}
        label="Verdict filter"
      />

      {pending > 0 && (
        <span className="text-xs text-muted-foreground">
          {pending} unknown — load more or refresh current evidence
        </span>
      )}
    </div>
  )
}

/** The NOK cohort sub-control, shown only when NOK is the active verdict: a
 *  second segmented control splitting NOK into Fixable (will list once filled),
 *  Excluded (business-rule won't-list), and Set aside (parked). The three counts
 *  partition the NOK total. Renders nothing when there are no NOK configs. */
export function NokCohortTabs({
  summary,
  value,
  onChange,
}: {
  summary: RunSkuSummary
  value: NokCohort
  onChange: (value: NokCohort) => void
}) {
  const { nok, nokFixable, nokBlocked, setAside } = summary
  if (nok === 0) {
    return null
  }

  const cohortOptions: readonly SegmentedOption<NokCohort>[] = [
    {
      value: "fixable",
      label: "Fixable",
      icon: Wrench,
      iconClassName: "text-warning",
      count: nokFixable,
    },
    {
      value: "excluded",
      label: "Excluded",
      icon: Ban,
      iconClassName: "text-destructive",
      count: nokBlocked,
    },
    {
      value: "set-aside",
      label: "Set aside",
      icon: Archive,
      iconClassName: "text-muted-foreground",
      count: setAside,
    },
  ]

  return (
    <SegmentedControl
      options={cohortOptions}
      value={value}
      onChange={onChange}
      label="NOK cohort"
    />
  )
}
